/**
 * PremiumPaymentGateway (src/ version)
 *
 * Redux-free version of the legacy PremiumPaymentGateway.
 * Data is sourced from React Query hooks instead of the Redux store.
 *
 * Handles the payment checkout flow as a modal.
 * Handles activation error.
 *
 * If isPremiumMidfunnelPayment is true, the payment modal will launch directly
 * (used from NextSteps upgradeToPremium flow).
 */
import { useEffect, useState } from 'react';
import { isPast } from 'date-fns';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserFreemiumWebApiHandler_details,
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
  useRentalManagerUserPropertySummaryWebApiHandler_nextSteps,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { invalidateListingsPageQueries } from '../../../hooks/queries/listings';
import { useMultiplexListingFlow } from '../../../hooks/queries/multiplex';
import { useActivateListing } from '../../../hooks/queries/useActivateListing';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

import FreemiumErrorModal from '../../modals/FreemiumErrorModal';
import PaymentModal from '../../modals/PaymentModal';
import PremiumMidfunnelSuccessModal from '../../modals/PremiumMidfunnelSuccessModal';

import { ZRM_GTM_EVENTS } from '../../../analytics/events/gtm';
import { emitCompleteUpgradeToPremiumEvent } from '../../../analytics/events/modularListing';
import {
  DEFAULT_ACTIVATE_MULTIPLEX_TRIGGER_OBJECT,
  emitActivateListingEvent,
  emitActivateMultiplexListingEvent,
  emitCompletesPremiumUpsellEvent,
} from '../../../analytics/events/payAndPublish';
import { trackGtmEvent } from '../../../analytics/index';
import { useAuth } from '../../AuthProvider';

import type {
  FreemiumChargeV2ResponseWebDto,
  FreemiumDetailsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { FreemiumDatarouterError } from '../../modals/FreemiumErrorModal';
import type { ActivateMultiplexTriggerObject } from '../../../analytics/events/payAndPublish';

type Props = {
  listingAlias: string;
  isActive: boolean;
  freemiumData?: FreemiumDetailsResponseWebDto;
  isPremiumMidfunnelPayment?: boolean;
  displayContext?: string;
  selectedProductType?: number;
  onClose: () => void;
  onActivateListing?: () => void;
  isInBuildingGroup: boolean;
  // Set by PayAndPublish from the button/footer that opened this gateway; used for multiplex activation clickstream.
  multiplexActivationTriggerObject?: ActivateMultiplexTriggerObject;
};

const MODAL_SWAP_DELAY = 100;

const PremiumPaymentGateway = ({
  listingAlias,
  isActive,
  freemiumData,
  isPremiumMidfunnelPayment = false,
  displayContext,
  selectedProductType = PRODUCT_TYPE_NAME_TO_ID.PREMIUM,
  onClose,
  isInBuildingGroup = false,
  onActivateListing,
  multiplexActivationTriggerObject = DEFAULT_ACTIVATE_MULTIPLEX_TRIGGER_OBJECT,
}: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const { refetch: refetchWebAnalytics } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: false,
    },
    { enabled: false },
  );

  const activeProduct = freemiumData?.activeProduct;
  const activeProductType = activeProduct?.type ?? PRODUCT_TYPE_NAME_TO_ID.FREE;
  const freemiumActiveProductTimestamp = activeProduct?.expirationTimestamp ?? '';
  const products = freemiumData?.products ?? [];

  const isActiveProductExpired = freemiumActiveProductTimestamp
    ? isPast(new Date(freemiumActiveProductTimestamp))
    : true;
  const isActiveProductPremiumAndNotExpired =
    activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && !isActiveProductExpired;

  const foundIndex = products.findIndex((product) => product.type === selectedProductType);
  const selectedProduct = foundIndex >= 0 ? products[foundIndex] : undefined;

  const { mutate: activate } = useActivateListing();

  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);
  const [publishError, setPublishError] = useState<{
    display: boolean;
    error?: FreemiumDatarouterError;
    onClose?: () => void;
  }>({ display: false });
  const [displayMidfunnelSuccessModal, setDisplayMidfunnelSuccessModal] = useState(false);

  // Run only on mount: open the payment modal unless the active product is already premium.
  // We intentionally omit isActiveProductPremiumAndNotExpired from deps to avoid re-triggering.
  useEffect(() => {
    if (isActiveProductPremiumAndNotExpired) {
      return;
    }
    setDisplayPaymentModal(true);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentionally omitting isActiveProductPremiumAndNotExpired to avoid re-triggering
  }, []);

  const invalidatePropertyCache = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertySummaryWebApiHandler_listing.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertySummaryWebApiHandler_nextSteps.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserFreemiumWebApiHandler_details.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' as const });
    invalidateListingsPageQueries(queryClient);
  };

  const handleActivationError = (error: FreemiumDatarouterError) => {
    setPublishError({
      display: true,
      error,
      onClose: () => {
        onClose();
        setPublishError({ display: false });
      },
    });
  };

  const activateListing = async (freemiumChargeResponse?: FreemiumChargeV2ResponseWebDto) => {
    const { data: preActivationWebAnalytics } = await refetchWebAnalytics();

    return activate(
      { postData: { listingAlias } },
      {
        onSuccess: (activateResponse) => {
          const csEventParams = {
            currentProperty: preActivationWebAnalytics ?? {},
            user: user ?? {},
          };

          if (isInMultiplexListingFlow) {
            emitActivateMultiplexListingEvent({
              ...csEventParams,
              triggerObjectNm: multiplexActivationTriggerObject,
            });
          } else {
            // multiplexActivationTriggerObject is unused outside the multiplex wizard.
            emitActivateListingEvent(csEventParams);
          }

          if (freemiumChargeResponse) {
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PAY_PUBLISH });
            emitCompletesPremiumUpsellEvent(csEventParams);
            onClose();
            emitCompleteUpgradeToPremiumEvent({
              currentProperty: { listingAlias, freemium: freemiumData },
              user: user ?? {},
              triggerSource: 'zrm_premium_confirm_upgrade_at_activation',
            });
          } else {
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });
            onClose();
          }

          onActivateListing?.();

          void activateResponse;
          invalidatePropertyCache();
        },
        onError: (error) => {
          handleActivationError(error as FreemiumDatarouterError);
        },
      },
    );
  };

  const handleFreemiumChargeSuccess = (response: FreemiumChargeV2ResponseWebDto) => {
    if (response.isPaymentValid) {
      if (!isPremiumMidfunnelPayment && !isActive) {
        activateListing(response);
      } else {
        setDisplayMidfunnelSuccessModal(true);
        setDisplayPaymentModal(false);

        emitCompletesPremiumUpsellEvent({
          currentProperty: { listingAlias },
          user: user ?? {},
        });

        if (!isActive) {
          emitCompleteUpgradeToPremiumEvent({
            currentProperty: { listingAlias },
            user: user ?? {},
            triggerSource: 'zrm_premium_confirm_upgrade_preactivation',
          });
        }

        invalidatePropertyCache();
      }
    }
  };

  const handleFreemiumErrorPublishForFreeClick = () => {
    setPublishError({ display: false });
    activateListing();
  };

  const handleFreemiumErrorTryAgainClick = () => {
    setPublishError({ display: false });

    setTimeout(() => {
      setDisplayPaymentModal(true);
    }, MODAL_SWAP_DELAY);
  };

  const handleFreemiumChargeError = (err: FreemiumDatarouterError) => {
    setDisplayPaymentModal(false);

    setTimeout(() => {
      setPublishError({
        display: true,
        error: err,
        onClose: () => {
          onClose();
          setPublishError({ display: false });
        },
      });
    }, MODAL_SWAP_DELAY);
  };

  void displayContext;
  void selectedProduct;

  return (
    <div data-testid="PremiumPaymentGateway">
      {publishError.display ? (
        <FreemiumErrorModal
          error={publishError?.error}
          onClose={publishError?.onClose ?? onClose}
          onTryAgainClick={handleFreemiumErrorTryAgainClick}
          onPublishForFreeClick={handleFreemiumErrorPublishForFreeClick}
          isPremiumMidfunnelPayment={isPremiumMidfunnelPayment}
          selectedProductType={selectedProductType}
        />
      ) : null}
      {displayPaymentModal && (
        <PaymentModal
          handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
          onFreemiumChargeError={handleFreemiumChargeError}
          listingAlias={listingAlias}
          isRenderedInPageView={false}
          onClose={() => {
            setDisplayPaymentModal(false);
            onClose();
          }}
          isInBuildingGroup={isInBuildingGroup}
        />
      )}
      {displayMidfunnelSuccessModal && (
        <PremiumMidfunnelSuccessModal
          onClose={() => {
            setDisplayMidfunnelSuccessModal(false);
            onClose();
          }}
          isActive={isActive}
        />
      )}
    </div>
  );
};

export default PremiumPaymentGateway;
